/**
 * `/api/threads` — person-to-person threads (gh #4661). A thread lives in its ThreadDO; each
 * member's thread list is an index of it. A thread is the personal 1:1 of a contact pair, a
 * personal group (Decision 5), or an org-linked 1:1 or group (Decision 4), which use the same
 * shapes: `org_id` and `org_name` name the org, the list row carries no preview, and live
 * frames and pushes carry no message text.
 *
 * ORG-LINKED THREADS (Decision 4). Only the org's staff — its owners and admins, a hub admin or
 * a team user of one of its hubs — create them and invite to them; anyone holding a role in
 * the org (staff, or an app user with an explicit hub-user grant) may join one by accepting an
 * invite. Reading and sending need a current role, checked on every request: a thread whose
 * org role is gone answers 404 and leaves the list and the badge until a role returns. An
 * org-linked 1:1 opens from a contact (`POST /api/threads/org-direct`); an org-linked group
 * invites contacts and typed emails. Every invite answers the same way, and one the invitee
 * cannot take (no role, no account, a block) ends silently after the answer (Decision 2).
 *
 * Messages are ordered by `seq`, the thread's own counter: pages, read cursors and unread
 * counts all use it, never a timestamp. A member sees only the messages visible to them: a
 * message a blocked person sends is stored visible only to its author (Decision 7), so two
 * members of one thread can see different last messages and counts.
 *
 * Names are display names only, never emails (Decision 11). A null `display_name` renders as
 * "WayAI user"; `deleted_user: true` renders as "Deleted user" (Decision 6).
 *
 * Identifiers carry a `people` prefix: "thread" already names the hub conversation view.
 */

import { z } from 'zod';
import { timestampSchema, uuidSchema } from './common';
import {
  peopleInviteEmailSchema,
  peopleUserIdSchema,
  type PeopleIncomingInvite,
  type PeoplePage,
} from './people-contacts';

// =============================================================================
// LIMITS (#4661 Caps, Limits). Code constants: raising one is a code change.
// =============================================================================

/** The longest message body, in characters. */
export const PEOPLE_THREAD_MESSAGE_MAX_LENGTH = 4_000;
/** Messages per page (≤ 50 × 4,000 characters per RPC: the 32 MiB rule). */
export const PEOPLE_THREAD_MESSAGES_PAGE_MAX = 50;
/** Rows per page of a member's thread list. */
export const PEOPLE_THREAD_PAGE_MAX = 100;
export const PEOPLE_THREAD_PAGE_DEFAULT = 50;
/** Thread-list rows the session snapshot carries; the rest page through `GET /api/threads`. */
export const PEOPLE_THREAD_HEAD_MAX = 20;
/** The longest preview of a thread's last message a list row carries, in characters. */
export const PEOPLE_THREAD_PREVIEW_MAX_LENGTH = 200;

/** Accepted members a group holds at most, its admins included (Decision 5). */
export const PEOPLE_GROUP_MEMBERS_MAX = 256;
/** The longest group name, in characters. */
export const PEOPLE_GROUP_NAME_MAX_LENGTH = 100;
/** The longest group avatar URL, in characters. */
export const PEOPLE_GROUP_AVATAR_URL_MAX_LENGTH = 2_048;
/**
 * Pending invites one admin may hold in one group; a new invite past it answers 429, which
 * depends only on that admin (their own invites are the only ones they see).
 */
export const PEOPLE_GROUP_INVITES_PER_ADMIN_MAX = 256;
/** Pending incoming group invites one user may hold; a new one past it is dropped silently. */
export const PEOPLE_GROUP_INCOMING_INVITES_MAX = 100;
/**
 * Member deliveries one user may cause per UTC day in groups: each message counts its
 * recipients (#4661 Limits), and so does a rename or a new avatar, which reach every member.
 * Past it such a change answers 429; when the count cannot be made it answers 503
 * (fail-closed). A delete is never refused (Decision 6). Within the limit its deliveries are
 * counted after it; past it (owner decision 4) it sends no `thread_message_deleted` or
 * `thread_messages_deleted` frame, and members' rows change only when it deleted the group's
 * latest message: a client refetches what it shows when it next opens the thread.
 */
export const PEOPLE_THREAD_DELIVERIES_PER_DAY = 50_000;

// -----------------------------------------------------------------------------
// Attachments: one file per message, stored under its thread's own prefix and opened only
// through a short-lived URL its thread mints for a current member (`POST
// /api/threads/:threadId/attachments/urls`). Deleting the message for everyone deletes the file.
// -----------------------------------------------------------------------------

/**
 * The largest file a message carries, in bytes. It travels base64 inside the message's own
 * request, whose body the route caps at the limit every route taking a base64 attachment
 * shares (`MAX_MESSAGE_BODY_BYTES`): this size's base64 fits there with room for the text.
 */
export const PEOPLE_THREAD_ATTACHMENT_MAX_BYTES = 7 * 1024 * 1024;
/** The longest attachment file name, in characters: the storage layer's own cap (`messageAttachment`). */
export const PEOPLE_THREAD_ATTACHMENT_NAME_MAX_LENGTH = 255;
/**
 * The bytes of the attachments one user may hold at once, across every thread: only files of
 * messages not deleted count, and deleting one frees its bytes. Past it a message with a file
 * answers 429 (`details.limit`: `PEOPLE_ATTACHMENT_STORAGE_LIMIT`), which depends only on the sender; when
 * the count cannot be read it answers 503 (fail-closed).
 */
export const PEOPLE_ATTACHMENT_STORAGE_PER_USER_BYTES = 1024 * 1024 * 1024;
/** `PEOPLE_THREAD_ATTACHMENT_MAX_BYTES` in whole megabytes, as the clients state it. */
export const PEOPLE_THREAD_ATTACHMENT_MAX_MB = Math.floor(PEOPLE_THREAD_ATTACHMENT_MAX_BYTES / (1024 * 1024));
/** The `details.limit` of the 429 a send with a file answers past the sender's storage. */
export const PEOPLE_ATTACHMENT_STORAGE_LIMIT = 'attachment_storage';
/** How long an attachment's URL opens its file, in seconds. */
export const PEOPLE_THREAD_ATTACHMENT_URL_TTL_SECONDS = 15 * 60;
/** Attachment URLs one request mints at most: one page of messages' worth. */
export const PEOPLE_THREAD_ATTACHMENT_URLS_MAX = PEOPLE_THREAD_MESSAGES_PAGE_MAX;
/** The image types a client draws in place; every other file shows as its name. */
export const PEOPLE_THREAD_INLINE_IMAGE_TYPES: readonly string[] = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];

// -----------------------------------------------------------------------------
// Org-linked threads (Decision 4) and org-admin erasure (#4661 owner decision 7).
// -----------------------------------------------------------------------------

/**
 * The orgs one role check reads at most: a list page's or a snapshot's distinct orgs (the
 * 32 MiB rule). A row of an org past it is left out of that one answer.
 */
export const PEOPLE_ORG_ROLE_CHECK_MAX = 100;
/** The threads one erasure step reaches: one page of the person's own thread index. */
export const PEOPLE_ORG_ERASURE_PAGE = 100;
/**
 * Erasures one org may request in 24 hours, pending ones included; past it 429, which depends
 * only on the org's own requests. A finished erasure keeps its place until its 24 hours pass, so
 * how soon one finishes is never visible through this cap.
 */
export const PEOPLE_ORG_ERASURES_PER_DAY = 20;

// =============================================================================
// REQUEST SCHEMAS
// =============================================================================

/** Every `/api/threads/:threadId…` route. */
export const peopleThreadIdParam = z.object({ threadId: uuidSchema });

/** DELETE /api/threads/:threadId/messages/:messageId */
export const peopleThreadMessageIdParam = z.object({ threadId: uuidSchema, messageId: uuidSchema });

/** GET /api/threads — an opaque cursor and a page size. */
export const peopleThreadListQuery = z.object({
  cursor: z.string().min(1).max(256).optional(),
  limit: z.coerce.number().int().min(1).max(PEOPLE_THREAD_PAGE_MAX).default(PEOPLE_THREAD_PAGE_DEFAULT),
});

/** GET /api/threads/:threadId/messages — the page of messages before `before_seq`. */
export const peopleThreadMessagesQuery = z.object({
  before_seq: z.coerce.number().int().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(PEOPLE_THREAD_MESSAGES_PAGE_MAX).default(PEOPLE_THREAD_MESSAGES_PAGE_MAX),
});

/**
 * A file sent with a message: its name and its bytes in base64 (a `data:` URL's prefix is
 * accepted). The server reads its type from the name and its size from the bytes, which may
 * be at most `PEOPLE_THREAD_ATTACHMENT_MAX_BYTES`.
 */
export const peopleThreadAttachmentUploadSchema = z
  .object({
    file_name: z.string().trim().min(1).max(PEOPLE_THREAD_ATTACHMENT_NAME_MAX_LENGTH),
    file_binary: z.string().min(1),
  })
  .strict();

/**
 * POST /api/threads/:threadId/messages. `client_message_id` makes a retried send idempotent:
 * a second send with the same id from the same author answers the first message. A message
 * carries text, a file (`attachment`), or both; its text may be empty only beside a file.
 */
export const peopleThreadSendBody = z
  .object({
    body: z.string().max(PEOPLE_THREAD_MESSAGE_MAX_LENGTH).default(''),
    client_message_id: uuidSchema.optional(),
    attachment: peopleThreadAttachmentUploadSchema.optional(),
  })
  .strict()
  .refine((b) => b.body.trim().length > 0 || b.attachment !== undefined, {
    message: 'must not be empty',
    path: ['body'],
  });

/** POST /api/threads/:threadId/attachments/urls — URLs for these attachments of the thread. */
export const peopleThreadAttachmentUrlsBody = z
  .object({ attachment_ids: z.array(uuidSchema).min(1).max(PEOPLE_THREAD_ATTACHMENT_URLS_MAX) })
  .strict();

/** POST /api/threads/:threadId/read — everything up to `seq` has been read. */
export const peopleThreadReadBody = z.object({ seq: z.number().int().min(0) }).strict();

/**
 * PATCH /api/threads/:threadId — this member's own settings. `muted`: no push, unread still
 * counts. `hidden`: out of the list; only a read-only thread can be hidden, and a new message
 * brings it back.
 */
export const peopleThreadSettingsBody = z
  .object({
    muted: z.boolean().optional(),
    hidden: z.boolean().optional(),
  })
  .strict()
  .refine((b) => b.muted !== undefined || b.hidden !== undefined, 'nothing to change');

// -----------------------------------------------------------------------------
// Groups (Decision 5). A personal group's members join only by accepting an invite from an
// admin, who picks from their own contacts.
// -----------------------------------------------------------------------------

/** A group's name: trimmed, not empty. */
export const peopleGroupNameSchema = z.string().trim().min(1).max(PEOPLE_GROUP_NAME_MAX_LENGTH);

/** A group's avatar: an https URL, like the other avatars of the app. */
export const peopleGroupAvatarUrlSchema = z
  .string()
  .max(PEOPLE_GROUP_AVATAR_URL_MAX_LENGTH)
  .url()
  .refine((s) => s.startsWith('https://'), 'must be an https URL');

/**
 * POST /api/threads — a new group, with the caller as its admin: a personal group, or, with
 * `org_id`, a group owned by that org, which only its staff create (Decision 4).
 */
export const peopleGroupCreateBody = z
  .object({
    name: peopleGroupNameSchema,
    avatar_url: peopleGroupAvatarUrlSchema.nullable().optional(),
    org_id: uuidSchema.optional(),
  })
  .strict();

/**
 * POST /api/threads/org-direct — the org-linked 1:1 between the caller, staff of `org_id`, and
 * `user_id`, one of their contacts: opened (created the first time) and its invite sent. The
 * answer is the caller's row, the same whether or not the contact can join (Decision 2).
 */
export const peopleOrgDirectBody = z
  .object({
    org_id: uuidSchema,
    user_id: peopleUserIdSchema,
  })
  .strict();

/** PATCH /api/threads/:threadId/group — an admin renames the group or sets its avatar. */
export const peopleGroupUpdateBody = z
  .object({
    name: peopleGroupNameSchema.optional(),
    avatar_url: peopleGroupAvatarUrlSchema.nullable().optional(),
  })
  .strict()
  .refine((b) => b.name !== undefined || b.avatar_url !== undefined, 'nothing to change');

/**
 * POST /api/threads/:threadId/invites — an admin invites one of their contacts (`user_id`), or,
 * in an org-linked group, a typed email (`email`).
 */
export const peopleGroupInviteBody = z.union([
  z.object({ user_id: peopleUserIdSchema }).strict(),
  z.object({ email: peopleInviteEmailSchema }).strict(),
]);

/**
 * POST /api/threads/org-erasures — an owner or admin of `org_id` erases, for everyone, every
 * message the person with `email` wrote in that org's threads (#4661 owner decision 7). The
 * answer is the same for every address: the erasure runs after it.
 */
export const peopleOrgErasureBody = z
  .object({
    org_id: uuidSchema,
    email: peopleInviteEmailSchema,
  })
  .strict();

/**
 * POST /api/threads/:threadId/leave. `delete_my_messages` erases every message the caller
 * wrote there, for everyone, as they leave (personal groups only).
 */
export const peopleGroupLeaveBody = z
  .object({ delete_my_messages: z.boolean().optional() })
  .strict();

/** DELETE /api/threads/:threadId/invites/:inviteId */
export const peopleGroupInviteIdParam = z.object({ threadId: uuidSchema, inviteId: uuidSchema });

/** POST /api/threads/invites/:inviteId/{accept,decline} — an invite to the caller. */
export const peopleGroupIncomingInviteIdParam = z.object({ inviteId: uuidSchema });

/** DELETE /api/threads/:threadId/members/:userId, POST …/members/:userId/admin */
export const peopleGroupMemberParam = z.object({ threadId: uuidSchema, userId: peopleUserIdSchema });

// =============================================================================
// RESPONSE SCHEMAS
// =============================================================================

/** `dm` is a 1:1; `group` is a named group of up to `PEOPLE_GROUP_MEMBERS_MAX` members. */
export const peopleThreadKindSchema = z.enum(['dm', 'group']);

/** A member as other members see them: a display name, never an email. */
export const peopleThreadMemberSchema = z
  .object({
    user_id: peopleUserIdSchema,
    display_name: z.string().nullable(),
    deleted_user: z.boolean(),
  })
  .strict();

/**
 * A thread's latest message as a list row shows it. `preview` is its text, or its file's name
 * when it has a file and no text; null for an org-linked thread (its rows carry no text,
 * Decision 11) and for a deleted message.
 */
export const peopleThreadLastMessageSchema = z
  .object({
    message_id: uuidSchema,
    seq: z.number().int().positive(),
    author_user_id: peopleUserIdSchema,
    preview: z.string().nullable(),
    deleted: z.boolean(),
    created_at: timestampSchema,
  })
  .strict();

/** A group member's role: admins invite, remove, promote, rename and set the avatar. */
export const peopleGroupRoleSchema = z.enum(['admin', 'member']);

/**
 * A group as one member's list row shows it.
 *
 * - `membership`: `member` while they belong to it. `removed`: an admin removed them from a
 *   personal group while messages of theirs remain there; the row is content-free (no
 *   preview, no unread, not writable, the name it had then), and its one action is deleting
 *   those messages (`POST /api/threads/:threadId/messages/delete-mine`). `left`: they left,
 *   or deleted their messages after a removal; the row is hidden and content-free.
 * - `role`: `admin` or `member` while they belong to it; null otherwise.
 * - `avatar_url`: the group's avatar while they belong to it; null otherwise.
 */
export const peopleGroupSummarySchema = z
  .object({
    membership: z.enum(['member', 'removed', 'left']),
    role: peopleGroupRoleSchema.nullable(),
    avatar_url: z.string().nullable(),
  })
  .strict();

/**
 * One row of a member's thread list, as that member sees the thread.
 *
 * - `title`: a group's name, or a 1:1's counterpart's display name; null renders from
 *   `counterpart` ("WayAI user", or "Deleted user").
 * - `counterpart`: the other member of a 1:1; null for a group.
 * - `org_id` / `org_name`: the owning org of an org-linked thread; null for a personal one.
 *   `org_name` is the org's name when the thread was created or last invited someone.
 * - `last_seq`: the `seq` of the last message this member can see (0 for none).
 * - `unread_count`: messages from others after `read_seq`, not deleted.
 * - `writable`: whether this member may send. A personal 1:1 is writable while the pair are
 *   contacts and this member does not block the other (Decision 3). A group, and an org-linked
 *   1:1, is writable for its members.
 * - `group`: a group's own fields, for this member; absent or null for a 1:1.
 */
export const peopleThreadSummarySchema = z
  .object({
    thread_id: uuidSchema,
    kind: peopleThreadKindSchema,
    org_id: z.string().nullable(),
    org_name: z.string().nullable(),
    title: z.string().nullable(),
    counterpart: peopleThreadMemberSchema.nullable(),
    last_seq: z.number().int().nonnegative(),
    last_message: peopleThreadLastMessageSchema.nullable(),
    last_activity_at: timestampSchema,
    read_seq: z.number().int().nonnegative(),
    unread_count: z.number().int().nonnegative(),
    muted: z.boolean(),
    hidden: z.boolean(),
    writable: z.boolean(),
    group: peopleGroupSummarySchema.nullable().optional(),
  })
  .strict();

/**
 * A message's file, as its viewers see it. Its bytes open only at a URL the thread mints
 * (`POST /api/threads/:threadId/attachments/urls`); `content_type` is read from the name.
 */
export const peopleThreadAttachmentSchema = z
  .object({
    attachment_id: uuidSchema,
    file_name: z.string(),
    content_type: z.string(),
    size_bytes: z.number().int().positive(),
  })
  .strict();

/**
 * One message. A deleted message stays as a tombstone: `body` null, `deleted` true, no
 * `attachment` (Decision 6). `client_message_id` is the author's own idempotency key, shown
 * only to the author. `attachment` is the message's file, null when it has none; a message
 * with a file may have an empty `body`. A client that predates attachments ignores it.
 */
export const peopleThreadMessageSchema = z
  .object({
    message_id: uuidSchema,
    seq: z.number().int().positive(),
    author_user_id: peopleUserIdSchema,
    body: z.string().nullable(),
    deleted: z.boolean(),
    created_at: timestampSchema,
    deleted_at: timestampSchema.nullable(),
    client_message_id: uuidSchema.nullable(),
    attachment: peopleThreadAttachmentSchema.nullable().optional(),
  })
  .strict();

/**
 * A group's member, as every member sees them: accepted members only, never a pending
 * invitee (Decision 5), and a display name, never an email (Decision 11).
 */
export const peopleGroupMemberSchema = peopleThreadMemberSchema
  .extend({ role: peopleGroupRoleSchema, joined_at: timestampSchema })
  .strict();

/**
 * One invite an admin sent, as only that admin sees it (Decision 5). It keeps this one shape
 * until it is accepted, cancelled or expires: a decline, a block, the invitee's own caps or,
 * in an org-linked group, an invitee with no role there end it silently, and the row stays
 * until it expires (Decisions 2, 4 and 7). `invitee` is the admin's own contact, named as the
 * admin's contact list names them. An invite by typed email (an org-linked group's) carries
 * the address as typed in `invitee_email`, and never an account: its `invitee` names the
 * invite (`user_id` is its `invite_id`, `display_name` the address), so a client that predates
 * `invitee_email` shows the address.
 */
export const peopleGroupSentInviteSchema = z
  .object({
    invite_id: uuidSchema,
    invitee: peopleThreadMemberSchema,
    invitee_email: z.string().nullable().optional(),
    status: z.literal('sent'),
    created_at: timestampSchema,
    expires_at: timestampSchema,
  })
  .strict();

/**
 * An invite to a group the caller holds: the group, and who is asking (their email too, so
 * the invitee knows who it is: Decision 11). `group.name` is the group's when the invite
 * reached the caller. It carries no avatar: an avatar is a URL the client fetches, and a fetch
 * the inviting admin can observe would tell them the invite was delivered (Decisions 2 and 7).
 *
 * An org-linked thread's invite names its org (`org`), and `kind` says whether it is a group's
 * or an org-linked 1:1's (`dm`), whose `group.name` is the org's name, so a client that predates
 * `kind` shows the org. Both are absent on a personal group's invite.
 */
export const peopleGroupIncomingInviteSchema = z
  .object({
    invite_id: uuidSchema,
    thread_id: uuidSchema,
    kind: peopleThreadKindSchema.optional(),
    group: z.object({ name: z.string() }).strict(),
    org: z.object({ org_id: z.string(), org_name: z.string().nullable() }).strict().nullable().optional(),
    inviter: z
      .object({ user_id: peopleUserIdSchema, display_name: z.string().nullable(), email: z.string().nullable() })
      .strict(),
    created_at: timestampSchema,
    expires_at: timestampSchema,
  })
  .strict();

export type PeopleThreadSettingsBody = z.infer<typeof peopleThreadSettingsBody>;
export type PeopleThreadAttachmentUpload = z.infer<typeof peopleThreadAttachmentUploadSchema>;
export type PeopleThreadAttachment = z.infer<typeof peopleThreadAttachmentSchema>;
export type PeopleGroupUpdateBody = z.infer<typeof peopleGroupUpdateBody>;
export type PeopleThreadKind = z.infer<typeof peopleThreadKindSchema>;
export type PeopleThreadMember = z.infer<typeof peopleThreadMemberSchema>;
export type PeopleThreadSummary = z.infer<typeof peopleThreadSummarySchema>;
export type PeopleThreadMessage = z.infer<typeof peopleThreadMessageSchema>;
export type PeopleGroupSummary = z.infer<typeof peopleGroupSummarySchema>;
export type PeopleGroupRole = z.infer<typeof peopleGroupRoleSchema>;
export type PeopleGroupMember = z.infer<typeof peopleGroupMemberSchema>;
export type PeopleGroupSentInvite = z.infer<typeof peopleGroupSentInviteSchema>;
export type PeopleGroupIncomingInvite = z.infer<typeof peopleGroupIncomingInviteSchema>;

/**
 * A page of messages, oldest first. `members` names every author on the page.
 * `next_before_seq` continues toward older messages; null when there are none.
 */
export interface PeopleThreadMessagesPage {
  messages: PeopleThreadMessage[];
  members: PeopleThreadMember[];
  next_before_seq: number | null;
}

/** A thread as its member opens it: the list row and the newest page of messages. */
export interface PeopleThreadView extends PeopleThreadMessagesPage {
  thread: PeopleThreadSummary;
}

/** GET /api/threads — newest activity first; hidden threads are left out. */
export type PeopleThreadsResponse = PeoplePage<PeopleThreadSummary>;
/** GET /api/threads/:threadId */
export interface PeopleThreadViewResponse {
  data: PeopleThreadView;
}
/** GET /api/threads/:threadId/messages */
export interface PeopleThreadMessagesResponse {
  data: PeopleThreadMessagesPage;
}
/** POST /api/threads/:threadId/messages, DELETE …/messages/:messageId */
export interface PeopleThreadMessageResponse {
  data: { message: PeopleThreadMessage; thread: PeopleThreadSummary };
}
/**
 * POST /api/threads/:threadId/read, PATCH /api/threads/:threadId; for a group also POST
 * /api/threads (created), PATCH …/group and POST /api/threads/invites/:inviteId/accept.
 */
export interface PeopleThreadSummaryResponse {
  data: PeopleThreadSummary;
}
/** GET /api/threads/:threadId/members — every accepted member, at most `PEOPLE_GROUP_MEMBERS_MAX`. */
export interface PeopleGroupMembersResponse {
  data: PeopleGroupMember[];
}
/** GET /api/threads/:threadId/invites — the caller's own pending invites to the group. */
export interface PeopleGroupSentInvitesResponse {
  data: PeopleGroupSentInvite[];
}
/** POST /api/threads/:threadId/invites — the same answer however the invite ends. */
export interface PeopleGroupSentInviteResponse {
  data: PeopleGroupSentInvite;
}
/** GET /api/threads/invites — capped at `PEOPLE_GROUP_INCOMING_INVITES_MAX`, one page. */
export interface PeopleGroupIncomingInvitesResponse {
  data: PeopleGroupIncomingInvite[];
}
/** POST /api/threads/:threadId/messages/delete-mine */
export interface PeopleGroupDeleteMineResponse {
  data: { deleted: number };
}
/** An org the caller may create org-linked threads in: one where they are staff. */
export interface PeopleThreadOrg {
  org_id: string;
  org_name: string | null;
}
/** GET /api/threads/orgs — the org picker's orgs. */
export interface PeopleThreadOrgsResponse {
  data: PeopleThreadOrg[];
}
/**
 * POST /api/threads/org-erasures — 202, the same for every address: whether it has an account,
 * and whether that person wrote anything in the org's threads, is never answered (Decision 2).
 */
export interface PeopleOrgErasureResponse {
  data: { erasure_id: string; status: 'pending' };
}
/**
 * One attachment's URL: it opens the file until `expires_at`
 * (`PEOPLE_THREAD_ATTACHMENT_URL_TTL_SECONDS` after it was minted), for whoever holds it.
 */
export interface PeopleThreadAttachmentUrl {
  attachment_id: string;
  url: string;
  expires_at: string;
}
/**
 * POST /api/threads/:threadId/attachments/urls — a URL for each asked attachment the caller
 * can see in the thread now; one they cannot (deleted, kept from them, or not the thread's)
 * is left out.
 */
export interface PeopleThreadAttachmentUrlsResponse {
  data: PeopleThreadAttachmentUrl[];
}

/**
 * The text a message shows where it is summed up in one line — a list row's preview, a push —
 * its text, or its file's name when it has no text. Every surface that previews a message
 * reads it here.
 */
export function peopleMessagePreview(body: string | null, fileName: string | null | undefined): string {
  return body ? body : fileName ?? '';
}

/** Whether a client draws an attachment of `contentType` in place (`PEOPLE_THREAD_INLINE_IMAGE_TYPES`). */
export function isPeopleInlineImage(contentType: string): boolean {
  return PEOPLE_THREAD_INLINE_IMAGE_TYPES.includes(contentType.toLowerCase());
}

// =============================================================================
// LIVE FRAMES (through the member's LiveUpdateSession)
//
// A frame can repeat a message an earlier one carried, with the message as it stands when the
// frame is built: one deleted meanwhile arrives as its tombstone. So a client keys messages by
// `message_id`, orders them by `seq`, and keeps a deletion once it has seen one. A member whose
// deliveries keep failing can miss message frames past the newest 50 owed to them; the thread
// row's `last_seq` (in any later frame, the list or the snapshot) says which to fetch. A
// delete-for-everyone frame is kept owed far longer (up to 1,000), since nothing else tells a
// client that a message it holds was deleted. A row that exhausts its retries is dropped with
// every frame it held; the member's next open of the thread repairs their row, and refetching
// the pages a client shows brings its messages up to date. In a group, a delete by a member past
// their daily deliveries (`PEOPLE_THREAD_DELIVERIES_PER_DAY`) sends no delete frame at all: the
// client sees the tombstone when it next fetches the page, and a row's unread count can stay
// higher until its member next opens the thread.
// =============================================================================

/** The live frame types of this surface. */
export const PEOPLE_LIVE_FRAME_TYPES = [
  'thread_message',
  'thread_updated',
  'thread_message_deleted',
  'thread_messages_deleted',
  'contact_invite',
  'group_invite',
] as const;

/**
 * `thread_message` — a message this member can see. An org-linked thread's frame is
 * content-free (`message: null`): the client fetches the message through the role-checked
 * `GET /api/threads/:threadId`.
 */
export type PeopleThreadMessageFrame =
  | { thread: PeopleThreadSummary; content_free: false; message: PeopleThreadMessage }
  | { thread: PeopleThreadSummary; content_free: true; message: null };

/** `thread_updated` — this member's list row changed (read, mute, hide, title, writability). */
export interface PeopleThreadUpdatedFrame {
  thread: PeopleThreadSummary;
}

/** `thread_message_deleted` — a message this member could see was deleted for everyone. */
export interface PeopleThreadMessageDeletedFrame {
  thread: PeopleThreadSummary;
  message_id: string;
  seq: number;
}

/**
 * `thread_messages_deleted` — every message `author_user_id` wrote in this group up to
 * `through_seq` was deleted for everyone at once (a member leaving with their messages, or a
 * removed member deleting theirs).
 */
export interface PeopleThreadMessagesDeletedFrame {
  thread: PeopleThreadSummary;
  author_user_id: string;
  through_seq: number;
}

/**
 * `contact_invite` — this member's pending incoming invites changed. `invite` is the one that
 * arrived, or null when one left (accepted, declined, cancelled or expired).
 */
export interface PeopleContactInviteFrame {
  pending_invite_count: number;
  invite: PeopleIncomingInvite | null;
}

/** Whether a live frame's `type` is one of this surface's (`PEOPLE_LIVE_FRAME_TYPES`). */
export function isPeopleLiveFrameType(type: string): type is (typeof PEOPLE_LIVE_FRAME_TYPES)[number] {
  return (PEOPLE_LIVE_FRAME_TYPES as readonly string[]).includes(type);
}

/** A people frame: its type and the payload that type carries. */
export type PeopleLiveFrame =
  | { type: 'thread_message'; payload: PeopleThreadMessageFrame }
  | { type: 'thread_updated'; payload: PeopleThreadUpdatedFrame }
  | { type: 'thread_message_deleted'; payload: PeopleThreadMessageDeletedFrame }
  | { type: 'thread_messages_deleted'; payload: PeopleThreadMessagesDeletedFrame }
  | { type: 'contact_invite'; payload: PeopleContactInviteFrame }
  | { type: 'group_invite'; payload: PeopleGroupInviteFrame };

/**
 * `group_invite` — this member's pending group invites changed. `invite` is the group's
 * invite as it now stands for them, or null when none is pending there (accepted, declined,
 * cancelled or expired). `thread_id` names the group either way.
 */
export interface PeopleGroupInviteFrame {
  thread_id: string;
  pending_group_invite_count: number;
  invite: PeopleGroupIncomingInvite | null;
}

// =============================================================================
// NAMES (Decisions 6 and 11) — how every surface names a person and a thread: the app's lists
// and threads (`@wayai/state` re-exports these) and the thread push (`offline-push.ts`). The
// fallback strings are each surface's own, already localized.
// =============================================================================

/** The strings a name falls back to, already localized. */
export interface PeopleNameLabels {
  wayaiUser: string;
  deletedUser: string;
}

/** A thread's title falls back one step further: a group row with no name. */
export interface PeopleThreadLabels extends PeopleNameLabels {
  group: string;
}

/** A name as typed: trimmed, and null when nothing is left. */
function nameOrNull(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

/** A person as others see them: "Deleted user", their display name, or "WayAI user". */
export function personName(
  person: Pick<PeopleThreadMember, 'display_name'> & { deleted_user?: boolean },
  labels: PeopleNameLabels,
): string {
  if (person.deleted_user) return labels.deletedUser;
  return nameOrNull(person.display_name) ?? labels.wayaiUser;
}

/**
 * A thread as its list row titles it: a group's name, or a 1:1's counterpart. A group row with
 * no name (one the viewer left, whose row no longer carries it) takes `labels.group`.
 */
export function threadTitle(
  row: Pick<PeopleThreadSummary, 'title' | 'counterpart'>,
  labels: PeopleThreadLabels,
): string {
  if (row.counterpart) {
    if (row.counterpart.deleted_user) return labels.deletedUser;
    return nameOrNull(row.title) ?? nameOrNull(row.counterpart.display_name) ?? labels.wayaiUser;
  }
  return nameOrNull(row.title) ?? labels.group;
}

/** An org-linked thread carries its org beside its title ("Ana · Acme"); a personal one, none. */
export function threadLabel(
  row: Pick<PeopleThreadSummary, 'title' | 'counterpart' | 'org_name'>,
  labels: PeopleThreadLabels,
): string {
  const title = threadTitle(row, labels);
  const org = nameOrNull(row.org_name);
  return org ? `${title} · ${org}` : title;
}

/**
 * The viewer's own name as the people surface records it: the profile's display name, then
 * the sign-in profile's name (`profile_name`), or null when both are empty — the order the
 * server reads them in (`UserDO.ownContactSnapshot`).
 */
export function ownPeopleName(profile: { displayName: string | null | undefined; profileName: string | null | undefined }): string | null {
  return nameOrNull(profile.displayName) ?? nameOrNull(profile.profileName);
}

// =============================================================================
// PUSH (a thread message to an offline member's phone)
//
// Its `data` names the thread and NEVER a `conversation_id`: a build that cannot open threads
// resolves a tap with no `conversation_id` to no navigation, while a thread id there would open
// a broken hub conversation. And it reaches only push tokens registered with the thread
// capability (`registerPushTokenBody.supports_threads`).
// =============================================================================

/** The `kind` of a thread push's `data`. */
export const PEOPLE_THREAD_PUSH_KIND = 'thread_message';

/** A thread push's `data`: what the app's tap handler opens. String-valued, as push data is. */
export type PeopleThreadPushData = {
  thread_id: string;
  kind: typeof PEOPLE_THREAD_PUSH_KIND;
};

// =============================================================================
// SESSION SNAPSHOT (`SessionSnapshot.people`)
// =============================================================================

/**
 * The people surface's first render, carried by the session snapshot while the surface is
 * on and only for an interactive sign-in session.
 */
export interface PeopleSessionSnapshot {
  /** The newest `PEOPLE_THREAD_HEAD_MAX` rows of the thread list. */
  threads: PeopleThreadSummary[];
  /** Continues the list through `GET /api/threads?cursor=`; null when the head is all of it. */
  next_cursor: string | null;
  /** Pending incoming contact invites. */
  pending_invite_count: number;
  /** Pending incoming group invites (`GET /api/threads/invites`). */
  pending_group_invite_count?: number;
  /** The thread the bootstrap's `active_thread_id` hint names, when the viewer is a member. */
  active_thread: PeopleThreadView | null;
}
